import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Text from '@embracesbs/component-text'
import Badge from '@embracesbs/component-badge/Badge'
import List from '@embracesbs/component-list/List'

import { FeatureFlag } from '../Interfaces/Types'
import { useApi, useAuth } from '../Hooks'
import Button2 from '@embracesbs/component-button2'
import Modal from '@embracesbs/component-modal'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import TextField from '@embracesbs/component-textfield'
import Toast, { customToast } from '@embracesbs/component-toast'
import EmptyState from '@embracesbs/component-emptystate'
import Icon from '@embracesbs/component-icon'
import Autocomplete from '@embracesbs/component-autocomplete'
import DarkTabs from '../Components/Common/DarkTabs'
import { DEVELOPER_CENTER_PATH } from '../Components/MainMenu/navigation'
import LoadingState from '../Components/Common/LoadingState'

const labelStyles: Record<string, { color: string; icon: string }> = {
  commercial: { color: '#730039', icon: 'BusinessDealCash2' },
  development: { color: '#0b525b', icon: 'ProgrammingBrowser' }
}

export default function FeatureFlags() {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[] | null>()
  const [selectedItem, setSelectedItem] = useState<FeatureFlag | null>()
  const [isUpdating, setIsUpdating] = useState(false)
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newTenant, setNewTenant] = useState('')
  const [enabledTenants, setEnabledTenants] = useState<string[]>([])

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [tenantToDelete, setTenantToDelete] = useState<string | null>(null)

  const [segments, setSegments] = useState<string[]>([])
  const [activeSegment, setActiveSegment] = useState<string>('')
  const [query, setQuery] = useState('')
  const [tenantQuery, setTenantQuery] = useState('')
  const [flagFilter, setFlagFilter] = useState<'all' | 'global' | 'noTenants'>('all')
  const [labelFilter, setLabelFilter] = useState('')
  const navigate = useNavigate()

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const response = await fetchApi({
        endpoint: `/api/featureflags/segments/${activeSegment}`,
        method: 'GET'
      })
      if (response !== undefined && response.length > 0) {
        setFeatureFlags(response.sort((a: FeatureFlag, b: FeatureFlag) => a.label.localeCompare(b.label) || a.name.localeCompare(b.name)))
      } else {
        setFeatureFlags([])
      }
    } catch (error) {
      console.error(error)
      customToast.error('Failed to fetch feature flags')
    }
  }, [fetchApi, activeSegment])

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await fetchApi({
          endpoint: `/api/featureflags/segments`,
          method: 'GET'
        })
        if (response && response.length > 0) {
          setSegments(response)
          const initialSegment = response[0]
          setActiveSegment(initialSegment)
        }
      } catch (error) {
        console.error('Error fetching segments:', error)
      }
    }

    if (isLoggedIn) {
      fetchSegments()
    }
  }, [isLoggedIn, fetchApi])

  useEffect(() => {
    if (!isLoggedIn || !activeSegment) return
    fetchFeatureFlags()
    setSelectedItem(null)
  }, [activeSegment, isLoggedIn, fetchFeatureFlags])

  useEffect(() => {
    if (selectedItem) {
      setEnabledTenants(selectedItem.enabledFor || [])
      setTenantQuery('')
    }
  }, [selectedItem])

  const handleItemClick = (name: string) => {
    const selected = featureFlags?.find((featureFlag) => featureFlag.name === name)
    setSelectedItem(selected || null)
  }

  // Keeps enabledTenants, the selected item and the flags list in sync after an
  // add/remove, so navigating away and back doesn't show stale data.
  const applyEnabledTenants = (tenants: string[]) => {
    setEnabledTenants(tenants)
    setSelectedItem((prev) => (prev ? { ...prev, enabledFor: tenants } : prev))
    setFeatureFlags((prev) => prev?.map((flag) => (flag.name === selectedItem?.name ? { ...flag, enabledFor: tenants } : flag)) ?? prev)
  }

  const handleAddTenant = async () => {
    if (activeSegment?.toLowerCase() !== 'alpha') {
      return
    }

    const tenantName = newTenant.trim()
    if (!tenantName) return

    setIsUpdating(true)

    fetchApi({
      endpoint: `/api/featureflags/${activeSegment}/${tenantName}`,
      method: 'PUT',
      body: {
        Name: selectedItem?.name || '',
        Enabled: true,
        Label: selectedItem?.label || ''
      }
    })
      .then(() => {
        applyEnabledTenants([...enabledTenants, tenantName])
        customToast.success(`Feature flag enabled for '${tenantName}'`)
        setNewTenant('')
        setIsModalOpen(false)
      })
      .catch((error) => {
        console.error('Error updating feature flag:', error)
        customToast.error(`Failed to enable the feature flag for '${tenantName}'`)
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }

  const handleRemoveTenant = async () => {
    if (activeSegment?.toLowerCase() !== 'alpha') {
      return
    }
    if (tenantToDelete) {
      setIsUpdating(true)

      fetchApi({
        endpoint: `/api/featureflags/${activeSegment}/${tenantToDelete}`,
        method: 'PUT',
        body: {
          Name: selectedItem?.name || '',
          Enabled: false,
          Label: selectedItem?.label || ''
        }
      })
        .then(() => {
          applyEnabledTenants(enabledTenants.filter((tenant) => tenant !== tenantToDelete))
          customToast.success(`Feature flag disabled for '${tenantToDelete}'`)
          setIsDeleteModalOpen(false)
        })
        .catch((error) => {
          console.error('Error disabling feature flag:', error)
          customToast.error(`Failed to disable the feature flag for '${tenantToDelete}'`)
        })
        .finally(() => {
          setIsUpdating(false)
        })
    }
  }

  const openDeleteModal = (tenantName: string) => {
    setTenantToDelete(tenantName)
    setIsDeleteModalOpen(true)
  }

  const handleTabChange = (segmentName: string) => {
    setActiveSegment(segmentName)
    setSelectedItem(null)
    setFeatureFlags(null)
    setLabelFilter('')
  }

  const normalizedQuery = query.trim().toLowerCase()
  const labelOptions = [
    { label: 'All labels', value: '' },
    ...Array.from(new Set(featureFlags?.map((flag) => flag.label) ?? [])).map((label) => ({ label, value: label }))
  ]
  const visibleFlags = featureFlags
    ?.filter((flag) => {
      if (labelFilter && flag.label !== labelFilter) return false
      if (flagFilter === 'global') return !flag.hasTenantFilter && flag.enabled
      if (flagFilter === 'noTenants') return flag.hasTenantFilter && (flag.enabledFor || []).length === 0
      return true
    })
    .filter(
      (flag) =>
        !normalizedQuery ||
        flag.name?.toLowerCase().includes(normalizedQuery) ||
        flag.label?.toLowerCase().includes(normalizedQuery) ||
        flag.description?.toLowerCase().includes(normalizedQuery) ||
        // Match tenant names too, so searching a tenant shows the flags enabled for it.
        (flag.enabledFor || []).some((tenant) => tenant.toLowerCase().includes(normalizedQuery))
    )

  if (segments.length === 0) {
    return (
      <div style={{ height: 'var(--app-height, 100vh)' }}>
        <LoadingState text='Loading segments…' />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: 'var(--app-height, 100vh)', flexDirection: 'row', overflow: 'hidden' }}>
      <Toast />
      {/* Left column: segment tabs (same compact 52px band as the dashboard) + flag list. */}
      <div style={{ width: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <DarkTabs
          tabs={segments.map((segment) => ({ id: segment, text: segment.charAt(0).toUpperCase() + segment.slice(1) }))}
          activeId={activeSegment}
          onTabClick={handleTabChange}
          backTo={DEVELOPER_CENTER_PATH}
        />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            backgroundColor: 'var(--tma-page-bg)',
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Flush white bar attached below the tabs: bleeds over the column padding on top/left/right. */}
          <div
            style={{
              flexShrink: 0,
              background: 'var(--tma-panel-bg)',
              borderBottom: '1px solid var(--tma-list-card-border)',
              padding: '10px',
              margin: '-10px -10px 8px'
            }}
          >
            <div className='search-no-glow' style={{ marginBottom: '8px' }}>
              <TextField
                isSearchMode
                placeholder='Search feature flags or tenant'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClear={() => setQuery('')}
                type='text'
                variant='default'
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  placeholder='All labels'
                  value={labelFilter}
                  onChange={(e: { value: string }) => setLabelFilter(e.value)}
                  options={labelOptions}
                />
              </div>
              <Button2
                text='Global'
                variant='filter'
                isActive={flagFilter === 'global'}
                onClick={() => setFlagFilter(flagFilter === 'global' ? 'all' : 'global')}
                type='button'
              />
              <Button2
                text='No tenants'
                variant='filter'
                isActive={flagFilter === 'noTenants'}
                onClick={() => setFlagFilter(flagFilter === 'noTenants' ? 'all' : 'noTenants')}
                type='button'
              />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} className='custom-scrollbar'>
            {!featureFlags ? (
              <LoadingState text='Loading feature flags…' />
            ) : visibleFlags?.length === 0 ? (
              <Text align='center' textColor='grey'>
                {normalizedQuery ? `No feature flags match '${query}'` : 'No feature flags match the current filter'}
              </Text>
            ) : (
              <List as='div' role='list' ariaLabel='Featureflags'>
                {visibleFlags?.map((featureFlag) => {
                  const isSelected = selectedItem?.name === featureFlag.name
                  return (
                    <div
                      key={`${featureFlag.label}:${featureFlag.name}`}
                      role='button'
                      tabIndex={0}
                      onClick={() => handleItemClick(featureFlag.name)}
                      onKeyDown={(e) => e.key === 'Enter' && handleItemClick(featureFlag.name)}
                      style={{
                        borderStyle: 'solid',
                        borderTopWidth: '1px',
                        borderRightWidth: '1px',
                        borderBottomWidth: '1px',
                        borderLeftWidth: '4px',
                        borderTopColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                        borderRightColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                        borderBottomColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                        borderLeftColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                        borderRadius: '6px',
                        background: 'var(--tma-panel-bg)',
                        padding: '10px 12px',
                        marginBottom: '6px',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s ease'
                      }}
                    >
                      <Text textStyle='body' content={featureFlag.name} singleLine hasTooltipEllipsis />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <Text textStyle='description' textColor='grey' content={featureFlag.label} />
                        {!featureFlag.hasTenantFilter && featureFlag.enabled && (
                          <span
                            style={{
                              flexShrink: 0,
                              background: '#029407',
                              color: '#ffffff',
                              borderRadius: '10px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 600,
                              lineHeight: 1.4,
                              fontFamily: "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                            }}
                          >
                            Global
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </List>
            )}
          </div>
        </div>
      </div>

      {/* Right column: flag details. */}
      <div
        style={{
          flex: 1,
          padding: '0 20px 20px',
          boxSizing: 'border-box',
          backgroundColor: 'var(--tma-panel-bg)',
          overflowY: 'auto'
        }}
      >
        {selectedItem ? (
          <div>
            {/* 52px title band aligning with the segment tab band on the left. */}
            <div style={{ height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Text textStyle='h6' style={{ fontSize: '16px' }} singleLine hasTooltipEllipsis>
                  {selectedItem.name}
                </Text>
                {Object.entries(selectedItem?.tags || {}).map(([key, value], index) => {
                  const style = key === 'Type' ? labelStyles[value.toLowerCase()] : null
                  return (
                    <Badge
                      key={index}
                      text={`${value}`}
                      color={style ? style.color : undefined}
                      innerColor='#ffffff'
                      size={14}
                      type='status'
                    />
                  )
                })}
              </div>
              <div style={{ borderTop: '1px solid var(--tma-list-card-border)', margin: '0 -20px' }} />
            </div>
            <Text textStyle='description' textColor='grey' style={{ margin: '10px 0 12px' }}>
              {selectedItem.description}
            </Text>
            <div style={{ marginTop: '10px' }}>
              <div style={{ marginTop: '10px' }}>
                {selectedItem.hasTenantFilter ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}
                    >
                      <Text textStyle='h6' style={{ fontSize: '16px' }}>{`Enabled Tenants (${enabledTenants.length})`}</Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className='search-no-glow' style={{ width: '240px' }}>
                          <TextField
                            isSearchMode
                            placeholder='Search tenants'
                            value={tenantQuery}
                            onChange={(e) => setTenantQuery(e.target.value)}
                            onClear={() => setTenantQuery('')}
                            type='text'
                            variant='default'
                          />
                        </div>
                        {activeSegment?.toLowerCase() === 'alpha' && (
                          <Button2
                            text='Enable for tenant'
                            onClick={() => setIsModalOpen(true)}
                            variant='contained'
                            buttonSize='30px'
                            buttonRadius='8px'
                            leftElement={<Icon iconName='Add' size={14} />}
                          />
                        )}
                      </div>
                    </div>
                    {(() => {
                      const normalizedTenantQuery = tenantQuery.trim().toLowerCase()
                      const visibleTenants = [...enabledTenants]
                        .sort((a, b) => a.localeCompare(b))
                        .filter((tenant) => !normalizedTenantQuery || tenant.toLowerCase().includes(normalizedTenantQuery))
                      if (enabledTenants.length === 0) {
                        return (
                          <Text textStyle='description' textColor='grey'>
                            This feature flag is not enabled for any tenant yet.
                          </Text>
                        )
                      }
                      if (visibleTenants.length === 0) {
                        return <Text textColor='grey'>No tenants match '{tenantQuery}'</Text>
                      }
                      return (
                        <div>
                          {visibleTenants.map((tenant) => (
                            <div
                              key={tenant}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                                padding: '3px 0 3px 8px',
                                borderBottom: '1px solid var(--tma-card-border)'
                              }}
                            >
                              <Text textStyle='body' content={tenant} singleLine hasTooltipEllipsis />
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                <Button2
                                  text=''
                                  aria-label={`Search for ${tenant}`}
                                  onClick={() => navigate(`/search?q=${encodeURIComponent(tenant)}`)}
                                  variant='outlined'
                                  buttonSize='30px'
                                  buttonRadius='8px'
                                  leftElement={<Icon iconName='Search' size={14} color='var(--tma-accent)' />}
                                />
                                {activeSegment?.toLowerCase() === 'alpha' && (
                                  <Button2
                                    text='Remove'
                                    onClick={() => openDeleteModal(tenant)}
                                    variant='outlined'
                                    isDanger
                                    buttonSize='30px'
                                    buttonRadius='8px'
                                    leftElement={<Icon iconName='Delete1' size={14} />}
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })()}
                  </>
                ) : selectedItem.enabled ? (
                  <div style={{ marginBottom: '15px' }}>
                    <Text textStyle='h6' style={{ fontSize: '16px' }}>
                      Globally Enabled
                    </Text>
                    <Text textStyle='description' textColor='grey'>
                      This feature flag is enabled for all tenants.
                    </Text>
                  </div>
                ) : (
                  <div style={{ marginBottom: '15px' }}>
                    <Text textStyle='h6' style={{ fontSize: '16px' }}>
                      Not Enabled
                    </Text>
                    <Text textStyle='description' textColor='grey'>
                      This feature flag is not currently enabled.
                    </Text>
                  </div>
                )}
                <Modal
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  headerTitle='Enable Featureflag for Tenant'
                  height={null}
                  variant='confirm'
                >
                  <TextFieldWrapper labelText='Tenant Name'>
                    <TextField
                      value={newTenant}
                      onChange={(e) => setNewTenant(e.target.value)}
                      placeholder='Enter tenant name'
                      pattern='^[a-zA-Z0-9-]{3,24}$'
                    />
                  </TextFieldWrapper>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <Button2
                      text='Add'
                      onClick={handleAddTenant}
                      variant='contained'
                      isDisabled={isUpdating || newTenant.trim() === ''}
                      isLoading={isUpdating}
                      style={{ width: '85px' }}
                    />
                  </div>
                </Modal>

                <Modal
                  isOpen={isDeleteModalOpen}
                  onClose={() => setIsDeleteModalOpen(false)}
                  headerTitle='Disable Featureflag for Tenant'
                  height={null}
                  variant='confirm'
                  onConfirm={handleRemoveTenant}
                >
                  <div>
                    Are you sure you want to disable the featureflag for <strong>{tenantToDelete}</strong>?
                  </div>
                </Modal>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState
              imageName='searchempty'
              size='medium'
              title='No feature flag selected'
              text='Select a feature flag from the list to see its details and enabled tenants.'
            />
          </div>
        )}
      </div>
    </div>
  )
}
